import type { TranslatedPOI } from '@/types/poi';
import type { POI } from '@inloco/shared';
import { useTranslation } from 'react-i18next';

/**
 * Hook to get the translation of a POI in the current language
 * @param poi - The POI with translations
 * @param fallbackLang - Fallback language (default: 'en')
 * @returns POI with translated name and description
 */
export function usePOITranslation(poi: POI, fallbackLang: string = 'en'): TranslatedPOI {
    const { i18n } = useTranslation();
    const currentLang = i18n.language;

    const getTranslation = (translations: Record<string, string> | undefined, fallback: string = ''): string => {
        if (!translations) return fallback;

        // Try with current language
        if (translations[currentLang]) {
            return translations[currentLang];
        }

        // Try with fallback language
        if (translations[fallbackLang]) {
            return translations[fallbackLang];
        }

        // Get first available translation
        const firstAvailable = Object.values(translations)[0];
        return firstAvailable || fallback;
    };

    return {
        ...poi,
        name: getTranslation(poi.name, poi.id),
        description: poi.description ? getTranslation(poi.description) : undefined,
        details: poi.details?.map(d => ({
            id: d.id,
            label: getTranslation(d.label),
            text: getTranslation(d.text),
        }))
    };
}

/**
 * Helper to translate an array of POIs
 * @param pois - Array of POIs
 * @param currentLang - Current language
 * @param fallbackLang - Fallback language (default: 'en')
 * @returns Array of translated POIs
 */
export function translatePOIs(
    pois: POI[],
    currentLang: string,
    fallbackLang: string = 'en'
): TranslatedPOI[] {
    const getTranslation = (translations: Record<string, string> | undefined, fallback: string = ''): string => {
        if (!translations) return fallback;

        if (translations[currentLang]) {
            return translations[currentLang];
        }

        if (translations[fallbackLang]) {
            return translations[fallbackLang];
        }

        const firstAvailable = Object.values(translations)[0];
        return firstAvailable || fallback;
    };

    return pois.map(poi => ({
        ...poi,
        name: getTranslation(poi.name, poi.id),
        description: poi.description ? getTranslation(poi.description) : undefined,
        details: poi.details?.map(d => ({
            id: d.id,
            label: getTranslation(d.label),
            text: getTranslation(d.text),
        }))
    }));
}
